import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import useTeamProductivityMeasurementData from './hooks/useTeamProductivityMeasurement';
import TeamProductivityMeasurementTable from './components/TeamProductivityMeasurementTable';
import FormTeamProductivityMeasurement from './createEditForm/components/FormTeamProductivityMeasurement';
import { useGetTeamProductivityMeasurementByIdQuery } from '../../api/endpoints/teamProductivityMeasurement';
import { useLanguage } from '../../contexts/LanguageContext';

const TeamProductivityMeasurement = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const navigate = useNavigate();

    const [dialogVisible, setDialogVisible] = useState(false);
    const [editId, setEditId] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const initialState = {
        oldRequestNumbers: [],
        requestSender: null,
        requestSenderName: '',
        requestBroadChannel: null,
        requestType: null,
        requestDetail: '',
        howItConcluded: '',
        lessonLearned: '',
        startDate: new Date(),
        endDate: null,
        vorVehicle: null,
        stakeholderUnit: null,
        requestSenderDealerId: null
    };

    const [formData, setFormData] = useState(initialState);
    const [submitted, setSubmitted] = useState(false);
    const isEditMode = !!editId;

    const { apiData, isLoading, lazyParams, sortField, sortOrder, onPage, onSort, activeFilters, handleFilterChange, initFilters, saveRecord, completeRecord, isCreating, isUpdating, isCompleting, refetch } = useTeamProductivityMeasurementData();

    const { data, isLoading: isLoadingById } = useGetTeamProductivityMeasurementByIdQuery({ id: editId, languageId: getCurrentLanguageId() }, { skip: !editId, refetchOnMountOrArgChange: true });

    useEffect(() => {
        if (isEditMode && data && data.id === editId) {
            const dealerObj = data.requestSenderDealerId
                ? {
                      id: data.requestSenderDealerId,
                      name: data.requestSenderName || '',
                      displayValue: data.requestSenderName || ''
                  }
                : null;

            setFormData({
                oldRequestNumbers: data.oldRequestNumbers || [],
                requestSender: data.requestSender || null,
                requestSenderName: data.requestSenderName || '',
                requestSenderDealer: dealerObj,
                requestSenderDealerId: data.requestSenderDealerId || null,
                requestBroadChannel: data.requestBroadChannel || null,
                requestType: data.requestType || null,
                requestDetail: data.requestDetail || '',
                howItConcluded: data.howItConcluded || '',
                lessonLearned: data.lessonLearned || '',
                startDate: data.startDate ? new Date(data.startDate) : new Date(),
                endDate: data.status === 15 ? (data.endDate ? new Date(data.endDate) : null) : new Date(),
                vorVehicle: data.vorVehicle || null,
                stakeholderUnit: data.stakeholderUnit || null
            });
            setIsInitialized(true);
        }
    }, [isEditMode, data, editId]);

    const handleChange = useCallback((key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleNew = () => {
        setEditId(null);
        setFormData(initialState);
        setSubmitted(false);
        setIsInitialized(false);
        setDialogVisible(true);
    };

    const handleEdit = (rowData) => {
        setEditId(rowData.id);
        setFormData({ ...initialState, endDate: null });
        setSubmitted(false);
        setIsInitialized(false);
        setDialogVisible(true);
    };

    const handleClose = () => {
        setDialogVisible(false);
        setEditId(null);
        setFormData(initialState);
        setSubmitted(false);
        setIsInitialized(false);
    };

    const dialogHeader = (
        <div className="flex align-items-center gap-2">
            <span>{th('teamProductivity.form.title')}</span>
            {isEditMode && data && (
                <span className={`px-2 py-1 border-round text-xs font-semibold ${data.status === 15 ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                    {data.status === 15 ? th('teamProductivity.status.completed') : th('teamProductivity.status.draft')}
                </span>
            )}
        </div>
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('teamProductivity.title')}</h1>
                <div className="flex gap-2">
                    <Button icon="pi pi-filter-slash" className="p-button-outlined p-button-secondary" onClick={initFilters} tooltip={th('filter.reset')} tooltipOptions={{ position: 'left' }} />
                    <Button label={th('common.add')} icon="pi pi-plus" onClick={handleNew} />
                </div>
            </div>

            <TeamProductivityMeasurementTable
                data={apiData?.items}
                totalCount={apiData?.totalCount}
                loading={isLoading}
                lazyParams={lazyParams}
                onPage={onPage}
                sortField={sortField}
                sortOrder={sortOrder}
                onSortChange={onSort}
                activeFilters={activeFilters}
                onFiltersChange={handleFilterChange}
                onResetFilters={initFilters}
                onEdit={handleEdit}
            />

            <Dialog header={dialogHeader} visible={dialogVisible} onHide={handleClose} style={{ width: '70vw' }} breakpoints={{ '960px': '90vw', '640px': '100vw' }} modal draggable={false} resizable={false}>
                {isEditMode && isLoadingById ? (
                    <div className="flex align-items-center justify-content-center" style={{ height: '300px' }}>
                        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                    </div>
                ) : (
                    <FormTeamProductivityMeasurement
                        id={editId}
                        data={data}
                        editMode={isEditMode}
                        submitted={submitted}
                        formData={formData}
                        handleChange={handleChange}
                        saveRecord={saveRecord}
                        completeRecord={completeRecord}
                        resetForm={() => {
                            setFormData(initialState);
                            setSubmitted(false);
                            setIsInitialized(false);
                        }}
                        setSubmitted={setSubmitted}
                        isCreating={isCreating}
                        isUpdating={isUpdating}
                        isCompleting={isCompleting}
                        onClose={handleClose}
                    />
                )}
            </Dialog>
        </div>
    );
};

export default TeamProductivityMeasurement;
